import type { TFunction } from 'i18next'
import { SITE_URL, BRAND, LOGO_URL } from '@/components/seo/seo'
import { marketingPage, type MarketingPageKey } from './links'
import { objectArrayKey } from './i18n-array'

/** Fixed publish date — never use Date.now() under the prerender/SSR. */
const DATE = '2026-06-12'

const absolute = (path: string) => `${SITE_URL}${path}`

/** The MindWeb Organization, reused as author and publisher. */
const organization = {
  '@type': 'Organization',
  name: BRAND,
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: LOGO_URL },
} as const

interface Crumb {
  readonly name: string
  readonly path: string
}

/**
 * BreadcrumbList for a marketing page. `trail` is the ancestor chain (Home is
 * prepended automatically); `pageKey` is the current page, whose name/path come
 * from the shared links catalogue so nothing is hardcoded twice.
 */
export function breadcrumbSchema(
  pageKey: MarketingPageKey,
  trail: readonly Crumb[] = [],
): Record<string, unknown> {
  const page = marketingPage(pageKey)
  const items = [
    { name: 'Home', path: '/' },
    ...trail,
    { name: page.crumb, path: page.to },
  ]
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absolute(item.path),
    })),
  }
}

/** TechArticle for the pillar and compare pages. */
export function articleSchema(args: {
  readonly headline: string
  readonly description: string
  readonly path: string
  readonly language: string
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: args.headline,
    description: args.description,
    inLanguage: args.language === 'zh' ? 'zh-CN' : 'en',
    datePublished: DATE,
    dateModified: DATE,
    author: organization,
    publisher: organization,
    mainEntityOfPage: { '@type': 'WebPage', '@id': absolute(args.path) },
  }
}

interface FaqItem {
  readonly question: string
  readonly answer: string
}

/**
 * FAQPage built from `marketing.<contentKey>.faq` (an array of {question,
 * answer}). Returns null when the key is absent so callers can skip rendering.
 */
export function faqSchema(
  t: TFunction,
  contentKey: MarketingPageKey,
): Record<string, unknown> | null {
  const items = objectArrayKey<FaqItem>(t, `marketing.${contentKey}.faq`)
  if (items.length === 0) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }
}
